import {
  BodyLong,
  Content,
  FlexDiv,
  Heading,
  Link,
  NavBar,
  PileDiv,
  VerticalSpace
  , Table
} from '@elhub/ds-components'
import styled from 'styled-components'

import { HorizontalLine } from 'components/HorizontalLine'
import TopContainer from 'components/TopContainer/TopContainer'

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link as NavLink, useNavigate, useParams } from 'react-router-dom'

import { useFetchMetaData } from '../hooks/useFetchMetaData'
import { LabelMessages } from '../locales/messages/label'
import { MaskinportenMessages } from '../locales/messages/maskinporten'
import { Dataset } from '../types/meta'

const ApiContent = styled(Content)`
  @media (min-width: 1200px) {
    width: 1200px;
  }
`

const Maskinporten = () => {
  const { t } = useTranslation()
  const { nav } = useParams()
  const navigate = useNavigate()
  const _nav = nav?.toString() ?? 'getting-started'

  const availableApis: Record<string, LabelMessages> = {
    'getting-started': LabelMessages.GETTING_STARTED,
    'create-client': LabelMessages.CREATE_CLIENT,
    scopes: LabelMessages.SCOPES
  }

  const setSubNav = (target: string) => navigate('/maskinporten/' + target)

  const addResourceType = (datasets: Dataset[], resourceType: string): Dataset[] =>
    datasets.map((dataset: Dataset) => ({ ...dataset, resource: resourceType }))

  const municipalitiesMetaData = useFetchMetaData('municipalities')
  const priceAreaMetaData = useFetchMetaData('price-areas')
  const gridAreaMetaData = useFetchMetaData('grid-areas')
  const meteringPointsMetaData = useFetchMetaData('metering-points')

  if (!municipalitiesMetaData || !priceAreaMetaData || !gridAreaMetaData || !meteringPointsMetaData) {
    return <div>Loading...</div>
  }

  const datasets: Dataset[] = [
    ...addResourceType(municipalitiesMetaData?.data.attributes.datasets ?? [], 'municipalities'),
    ...addResourceType(priceAreaMetaData?.data.attributes.datasets ?? [], 'price-areas'),
    ...addResourceType(gridAreaMetaData?.data.attributes.datasets ?? [], 'grid-areas'),
    ...addResourceType(meteringPointsMetaData?.data.attributes.datasets ?? [], 'metering-points')
  ].filter((dataset: Dataset) => !dataset.openDataSet)

  const descriptions: Record<string, JSX.Element> = {
    'getting-started': (
      <>
        <Heading spacing>{t(availableApis[_nav] as any)}</Heading>
        <HorizontalLine />
        <VerticalSpace />
        <BodyLong spacing>
          <Trans
            i18nKey={MaskinportenMessages.GETTING_STARTED_PARAGRAPH_1}
            components={{
              link1: (
                <Link
                  as={NavLink}
                  to='https://samarbeid.digdir.no/maskinporten/maskinporten/25'
                  target='_new'
                />
              )
            }}
          />
        </BodyLong>
        <BodyLong spacing style={{ color: 'red', fontStyle: 'italic' }}>
          <Trans
            i18nKey={MaskinportenMessages.GETTING_STARTED_PARAGRAPH_2}
            components={{
              link1: <Link as={NavLink} to='https://elhub.no/aktorer-og-markedsstruktur' target='_new' />
            }}
          />
        </BodyLong>
        <BodyLong spacing>{t(MaskinportenMessages.GETTING_STARTED_PARAGRAPH_3 as any)}</BodyLong>
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.GETTING_STARTED_SECTION_1_TITLE as any)}
        </Heading>
        <BodyLong spacing>
          <Trans
            i18nKey={MaskinportenMessages.GETTING_STARTED_SECTION_1_DESC}
            components={{
              link1: (
                <Link
                  as={NavLink}
                  to='https://samarbeid.digdir.no/maskinporten/konsument/119'
                  target='_new'
                />
              )
            }}
          />
        </BodyLong>
        <VerticalSpace />
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.GETTING_STARTED_SECTION_2_TITLE as any)}
        </Heading>
        <BodyLong spacing>
          <Trans
            i18nKey={MaskinportenMessages.GETTING_STARTED_SECTION_2_DESC_1}
            components={{
              link1: (
                <Link
                  as={NavLink}
                  to='https://docs.digdir.no/docs/Maskinporten/maskinporten_sjolvbetjening_web.html'
                  target='_new'
                />
              )
            }}
          />
        </BodyLong>
        <BodyLong spacing>
          <ul>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.GETTING_STARTED_SECTION_2_LI_1 as any}
                components={{
                  link1: <Link as={NavLink} to='https://samarbeid.digdir.no' target='_new' />
                }}
              />
            </li>
            <li>{t(MaskinportenMessages.GETTING_STARTED_SECTION_2_LI_2 as any)}</li>
            <li>{t(MaskinportenMessages.GETTING_STARTED_SECTION_2_LI_3 as any)}</li>
            <li>{t(MaskinportenMessages.GETTING_STARTED_SECTION_2_LI_4 as any)}</li>
            <li>{t(MaskinportenMessages.GETTING_STARTED_SECTION_2_LI_5 as any)}</li>
          </ul>
        </BodyLong>
        <BodyLong spacing>
          <Trans
            i18nKey={MaskinportenMessages.GETTING_STARTED_SECTION_2_DESC_2}
            components={{
              link1: (
                <Link
                  as={NavLink}
                  to='https://docs.digdir.no/docs/Maskinporten/maskinporten_feilsoking'
                  target='_new'
                />
              ),
              link2: <Link as={NavLink} to='mailto:servicedesk@digdir.no' />
            }}
          />
        </BodyLong>
        <VerticalSpace />
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.GETTING_STARTED_SECTION_3_TITLE as any)}
        </Heading>
        <BodyLong spacing>{t(MaskinportenMessages.GETTING_STARTED_SECTION_3_DESC as any)}</BodyLong>
        <VerticalSpace />
      </>
    ),
    'create-client': (
      <>
        <Heading spacing>{t(availableApis[_nav] as any)}</Heading>
        <HorizontalLine />
        <VerticalSpace />
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_TITLE as any)}
        </Heading>
        <BodyLong spacing>
          <ul>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_1}
                components={{
                  link1: <Link as={NavLink} to='mailto:post@elhub.no' />
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_2}
                components={{
                  link1: <Link as={NavLink} to='https://samarbeid.digdir.no/' target='_new' />
                }}
              />
            </li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_3 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_4 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_5 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_6 as any)}</li>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_7}
                components={{
                  link1: <Link as={NavLink} to='mailto:post@elhub.no' />
                }}
              />
            </li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_8 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_9 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_10 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_11 as any)}</li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_1_LI_12 as any)}</li>
          </ul>
        </BodyLong>
        <VerticalSpace />
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.CREATE_CLIENT_SECTION_2_TITLE as any)}
        </Heading>
        <BodyLong>
          <ul>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_2_LI_1}
                components={{
                  link1: (
                    <Link
                      as={NavLink}
                      to='https://docs.digdir.no/docs/Maskinporten/maskinporten_guide_apikonsument.html'
                      target='_new'
                    />
                  )
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_2_LI_2}
                components={{
                  link1: (
                    <Link
                      as={NavLink}
                      to='https://github.com/felleslosninger/jwt-grant-generator'
                      target='_new'
                    />
                  )
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey={MaskinportenMessages.CREATE_CLIENT_SECTION_2_LI_3}
                components={{
                  link1: <Link as={NavLink} to='/api/' target='_new' />
                }}
              />
            </li>
            <li>{t(MaskinportenMessages.CREATE_CLIENT_SECTION_2_LI_4 as any)}</li>
          </ul>
        </BodyLong>
      </>
    ),
    scopes: (
      <>
        <Heading spacing>{t(availableApis[_nav] as any)}</Heading>
        <HorizontalLine />
        <VerticalSpace />
        <BodyLong spacing>{t(MaskinportenMessages.SCOPES_PARAGRAPH_1_PART_1 as any as any)}</BodyLong>
        <BodyLong spacing>{t(MaskinportenMessages.SCOPES_PARAGRAPH_1_PART_2 as any as any)}</BodyLong>
        <ul>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_1_LI_1 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_1_LI_2 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_1_LI_3 as any)}</li>
        </ul>
        <VerticalSpace />
        <BodyLong spacing style={{ color: 'red', fontStyle: 'italic' }}>
          {t(MaskinportenMessages.SCOPES_PARAGRAPH_2_PART_1 as any as any)}
        </BodyLong>
        <VerticalSpace size='2' />
        <Heading size='medium' spacing>
          {t(MaskinportenMessages.SCOPES_PARAGRAPH_2_TITLE as any)}
        </Heading>
        <BodyLong spacing>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_DESC as any)}</BodyLong>
        <ul>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_1 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_2 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_3 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_4 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_5 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_6 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_7 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_8 as any)}</li>
          <li>{t(MaskinportenMessages.SCOPES_PARAGRAPH_2_LI_9 as any)}</li>
        </ul>
        <VerticalSpace size='3' />
        <Heading size='small'>Datasets</Heading>
        <VerticalSpace size='1' />
        <Table size='small' zebraStripes>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader scope='col'>Resource</Table.ColumnHeader>
              <Table.ColumnHeader scope='col'>Dataset</Table.ColumnHeader>
              <Table.ColumnHeader scope='col'>Access to Scopes</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {datasets.map(({ resource, name, scopes }) => (
              <Table.Row key={name}>
                <Table.DataCell scope='row' style={{ width: '150px', overflowWrap: 'anywhere' }}>
                  {`/${resource}`}
                </Table.DataCell>
                <Table.DataCell>{name}</Table.DataCell>
                <Table.DataCell>{(scopes ?? []).join(', ')}</Table.DataCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </>
    )
  }

  const navbarActive = ['getting-started', 'create-client', 'scopes'].indexOf(_nav)

  return (
    <TopContainer>
      <ApiContent>
        <VerticalSpace size='2' />
        <Heading size='large' spacing>
          {t(LabelMessages.MASKINPORTEN as any)}
        </Heading>
        <VerticalSpace size='2' />
        <FlexDiv>
          <FlexDiv style={{ marginRight: '3rem' }}>
            <NavBar orientation='vertical' defaultValue={navbarActive}>
              <VerticalSpace />
              <NavBar.Link onClick={() => setSubNav('getting-started')}>
                {t(LabelMessages.GETTING_STARTED as any)}
              </NavBar.Link>
              <NavBar.Link onClick={() => setSubNav('create-client')}>
                {t(LabelMessages.CREATE_CLIENT as any)}
              </NavBar.Link>
              <NavBar.Link onClick={() => setSubNav('scopes')}>{t(LabelMessages.SCOPES as any)}</NavBar.Link>
            </NavBar>
          </FlexDiv>
          <PileDiv style={{ flex: 2 }}>{descriptions[_nav]}</PileDiv>
        </FlexDiv>
      </ApiContent>
    </TopContainer>
  )
}

export default Maskinporten
